import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';

const LOGO = 'https://static.readdy.ai/image/1e1c36a8fe2eee6efab369c345d12000/d2de7d124fc44cc7dd03173e7e2df5a4.png';

const navLinks = [
  { label: 'El Manifiesto', href: '#manifiesto' },
  { label: 'La Cartografía', href: '#cartografia' },
  { label: 'Resonancia', href: '#resonancia' },
  { label: 'Planes', href: '#planes' },
];

interface NavbarProps {
  onOpenLogin: () => void;
}

export default function Navbar({ onOpenLogin }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNav = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-[#1A1C20]/95 backdrop-blur-md border-b border-[#8B9D83]/20' : 'bg-transparent'
      }`}
      style={{ height: '72px' }}
    >
      <div className="flex items-center justify-between h-full px-8 max-w-screen-2xl mx-auto">
        {/* Logo */}
        <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="flex items-center gap-3 cursor-pointer">
          <img src={LOGO} alt="Nube Geodésica" className="h-10 w-auto object-contain" />
        </a>

        {/* Center Nav */}
        <div className="hidden md:flex items-center gap-10">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNav(e, link.href)}
              className="text-[#F8F6F3]/60 hover:text-[#F8F6F3] text-xs uppercase tracking-[2px] transition-colors duration-300 relative group whitespace-nowrap cursor-pointer"
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#C97B63] group-hover:w-full transition-all duration-300" />
            </a>
          ))}
        </div>

        {/* CTAs */}
        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 text-[#8B9D83] hover:text-[#F8F6F3] text-xs uppercase tracking-[2px] font-semibold px-4 py-2.5 rounded-full border border-[#8B9D83]/30 hover:border-[#8B9D83]/70 hover:bg-[#8B9D83]/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <div className="w-2 h-2 rounded-full bg-[#8B9D83] animate-pulse" />
                Control Center
              </button>
              <button
                onClick={async () => { await signOut(); }}
                className="flex items-center gap-2 text-[#F8F6F3]/50 hover:text-[#C97B63] text-xs uppercase tracking-[2px] font-semibold px-4 py-2.5 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-logout-box-line text-sm" />
                Salir
              </button>
            </>
          ) : (
            <>
              {/* ACCEDER button */}
              <button
                onClick={onOpenLogin}
                className="flex items-center gap-2 text-[#F8F6F3]/70 hover:text-[#F8F6F3] text-xs uppercase tracking-[2px] font-semibold px-4 py-2.5 rounded-full border border-[#F8F6F3]/15 hover:border-[#8B9D83]/60 hover:bg-[#8B9D83]/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-login-box-line text-sm" />
                Acceder
              </button>

              {/* Register CTA */}
              <a
                href="#iniciar-trazado"
                onClick={(e) => handleNav(e, '#iniciar-trazado')}
                className="flex items-center gap-2 bg-[#C97B63] text-[#F8F6F3] text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-[#b86d55] transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Iniciar Trazado
                <i className="ri-arrow-right-line text-sm" />
              </a>
            </>
          )}
        </div>

        {/* Mobile menu toggle */}
        <button
          className="md:hidden text-[#F8F6F3] text-2xl cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={menuOpen ? 'ri-close-line' : 'ri-menu-line'} />
        </button>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div className="md:hidden bg-[#1A1C20]/98 border-b border-[#8B9D83]/20 px-8 py-6 flex flex-col gap-5">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNav(e, link.href)}
              className="text-[#F8F6F3]/70 hover:text-[#C97B63] text-sm uppercase tracking-[2px] transition-colors duration-300 cursor-pointer"
            >
              {link.label}
            </a>
          ))}
          <button
            onClick={() => { onOpenLogin(); setMenuOpen(false); }}
            className="inline-flex items-center gap-2 text-[#F8F6F3]/70 text-sm font-semibold px-6 py-3 rounded-full border border-[#F8F6F3]/15 w-fit cursor-pointer whitespace-nowrap"
          >
            <i className="ri-login-box-line" /> Acceder
          </button>
          <a
            href="#iniciar-trazado"
            onClick={(e) => handleNav(e, '#iniciar-trazado')}
            className="inline-flex items-center gap-2 bg-[#C97B63] text-[#F8F6F3] text-sm font-semibold px-6 py-3 rounded-full w-fit cursor-pointer whitespace-nowrap"
          >
            Iniciar Trazado <i className="ri-arrow-right-line" />
          </a>
        </div>
      )}
    </nav>
  );
}
